import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { primaryColor } from '../styles/GlobalStyles';

const SwitcherContainer = styled.div`
  position: relative;
  margin-left: 15px;
`;

const CurrentLanguage = styled.button`
  background: transparent;
  color: #333;
  border: none;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.3s ease;
  border-radius: 20px;
  
  &:hover {
    background-color: rgba(66, 141, 231, 0.1);
    color: ${primaryColor};
  }
  
  &:focus {
    outline: none;
  }
`;

const FlagIcon = styled.span`
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ChevronIcon = styled.span`
  margin-left: 2px;
  transition: transform 0.3s ease;
  transform: ${props => props.isOpen ? 'rotate(90deg)' : 'rotate(0)'};
  display: inline-block;
  font-size: 10px;
  color: ${props => props.isOpen ? primaryColor : '#888'};
`;

const LanguageDropdown = styled.div`
  position: absolute;
  top: calc(100% + 5px);
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
  min-width: 160px;
  z-index: 1000;
  overflow: hidden;
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  opacity: ${props => props.isOpen ? 1 : 0};
  transform: ${props => props.isOpen ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.95)'};
  transform-origin: top right;
  transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  
  &:before {
    content: '';
    position: absolute;
    top: -4px;
    right: 15px;
    width: 8px;
    height: 8px;
    background: white;
    transform: rotate(45deg);
    box-shadow: -2px -2px 5px rgba(0, 0, 0, 0.04);
  }
`;

const LanguageOption = styled.button`
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  padding: 12px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  transition: all 0.2s ease;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    left: 15px;
    bottom: 5px;
    width: 0;
    height: 1px;
    background-color: ${primaryColor};
    transition: all 0.3s ease;
    opacity: 0.7;
  }
  
  &:hover {
    background: #f8f9fa;
    color: ${primaryColor};
    
    &:after {
      width: calc(100% - 30px);
    }
  }
  
  &.active {
    background-color: rgba(66, 141, 231, 0.08);
    color: ${primaryColor};
    font-weight: 500;
    
    &:after {
      width: calc(100% - 30px);
    }
  }
  
  .flag {
    font-size: 20px;
  }
  
  .lang-name {
    font-weight: inherit;
  }
`;

const LanguageSwitcher = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const currentLanguage = i18n.language;
  
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsOpen(false);
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Get flag based on language
  const getFlag = (lang) => {
    return lang === 'en' ? '🇺🇸' : '🇻🇳';
  };
  
  return (
    <SwitcherContainer ref={dropdownRef}>
      <CurrentLanguage onClick={toggleDropdown}>
        <FlagIcon>{getFlag(currentLanguage)}</FlagIcon>
        <span>{currentLanguage === 'en' ? 'English' : 'Tiếng Việt'}</span>
        <ChevronIcon isOpen={isOpen}>▶</ChevronIcon>
      </CurrentLanguage>
      
      <LanguageDropdown isOpen={isOpen}>
        <LanguageOption 
          onClick={() => changeLanguage('en')}
          className={currentLanguage === 'en' ? 'active' : ''}
        >
          <span className="flag">🇺🇸</span>
          <span className="lang-name">{t('common.english')}</span>
        </LanguageOption>
        
        <LanguageOption 
          onClick={() => changeLanguage('vi')}
          className={currentLanguage === 'vi' ? 'active' : ''}
        >
          <span className="flag">🇻🇳</span>
          <span className="lang-name">{t('common.vietnamese')}</span>
        </LanguageOption>
      </LanguageDropdown>
    </SwitcherContainer>
  );
};

export default LanguageSwitcher; 